import { FieldProse, NumberedSection, RegaraNoteList, withInlinePlaceholders } from './DossierPrimitives';

/**
 * EU IVDR (Reg 2017/746) Conformity Assessment Route (Articles 48–50) renderer.
 * Parity: backend/services/dossier/eu_ivdr_conformity_route_renderer
 * (`conformity_route_sections_html`). The IVDR shape differs from MDR — the route
 * is a single string plus yes/no status flags, not primary/alternative objects.
 */

export interface IvdrConformityResult {
  device_class?: string;
  primary_route?: string | null;
  notified_body_involved?: boolean;
  eu_reference_laboratory?: boolean;
  batch_verification?: boolean;
  common_specifications?: boolean;
  consultations?: string[];
  output_chain?: string[];
  notes?: string[];
}

function yn(v: unknown): string {
  return v ? 'Yes' : 'No';
}

export function IvdrConformityRouteRenderer({ result }: { result: IvdrConformityResult }) {
  const consultations = Array.isArray(result.consultations) ? result.consultations : [];
  const outputChain = Array.isArray(result.output_chain) ? result.output_chain : [];
  const notes = Array.isArray(result.notes) ? result.notes : [];

  return (
    <div className="space-y-4">
      <NumberedSection prefix="Section" num={1} title="Conformity Assessment Route">
        <FieldProse
          label="Route:"
          body={result.primary_route ?? null}
          fallbackHint="conformity route"
        />
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1.5 text-[13px]">
          <div>
            <span className="font-semibold text-ink/80">Notified Body involved:</span>{' '}
            {yn(result.notified_body_involved)}
          </div>
          <div>
            <span className="font-semibold text-ink/80">EU reference laboratory verification:</span>{' '}
            {yn(result.eu_reference_laboratory)}
          </div>
          <div>
            <span className="font-semibold text-ink/80">Batch verification (Article 48):</span>{' '}
            {yn(result.batch_verification)}
          </div>
          <div>
            <span className="font-semibold text-ink/80">Common specifications:</span>{' '}
            {yn(result.common_specifications)}
          </div>
        </div>
      </NumberedSection>

      <NumberedSection prefix="Section" num={2} title="Consultations and Additional Requirements">
        {consultations.length === 0 ? (
          <p className="text-[13.5px] text-muted italic m-0">
            No additional consultations applicable.
          </p>
        ) : (
          <ul className="list-disc pl-5 m-0 space-y-1 text-[13.5px]">
            {consultations.map((c, i) => (
              <li key={i}>{withInlinePlaceholders(String(c))}</li>
            ))}
          </ul>
        )}
      </NumberedSection>

      <NumberedSection prefix="Section" num={3} title="Output Chain">
        {outputChain.length === 0 ? (
          <p className="text-[13.5px] text-muted italic m-0">No output steps specified.</p>
        ) : (
          <ol className="list-decimal pl-5 m-0 space-y-2 text-[13.5px]">
            {outputChain.map((s, i) => (
              <li key={i}>{withInlinePlaceholders(String(s))}</li>
            ))}
          </ol>
        )}
      </NumberedSection>

      <RegaraNoteList notes={notes} />
    </div>
  );
}
